import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Palette, Dna, Package2, FileText, Plus, Trash2,
  ArrowUp, ArrowDown, Eye, Sparkles, Image as ImageIcon, Upload,
  CheckCircle2, XCircle, AlertTriangle, Rocket, ClipboardCheck,
} from "lucide-react";
import api, { notifyError, imageUrl } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import TemplateLivePreview from "./TemplateLivePreview";

const CATEGORIES = ["Retail & Ecommerce", "Fashion", "Beauty", "Food & Beverage",
  "Automotive", "Home & Living", "Electronics", "Sports", "Vape"];
const FONTS = ["Inter", "Poppins", "Montserrat", "Roboto Slab", "Playfair Display", "Nunito"];
const inputCls = "w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm focus:border-cyan-500/50 outline-none";
const labelCls = "text-[10px] uppercase tracking-widest text-white/50 font-black mb-1 block";

const Field = ({ label, children }) => (
  <div><label className={labelCls}>{label}</label>{children}</div>
);

const ColorField = ({ label, value, onChange, testid }) => (
  <Field label={label}>
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
        className="w-9 h-9 rounded-lg border border-white/10 bg-transparent cursor-pointer" data-testid={testid} />
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} font-mono`} />
    </div>
  </Field>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <Field label={label}>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </Field>
);

/* ============ BRANDING TAB (Logo & Thumbnail Uploader) ============ */
const AssetUploader = ({ id, label, value, hint, onChange, uploading, setUploading, previewClass }) => {
  const inputRef = useRef(null);
  const pick = () => inputRef.current?.click();
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/platform/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onChange(r.data.url || r.data.path);
      toast.success(`${label} berhasil diupload.`);
    } catch (e) {
      notifyError(e, `Gagal upload ${label}`);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-4" data-testid={id}>
      <div className="flex items-center justify-between mb-3">
        <span className={labelCls + " !mb-0"}>{label}</span>
        {value ? (
          <span className="text-[10px] font-black uppercase tracking-widest text-emerald-300">Tersedia</span>
        ) : (
          <span className="text-[10px] font-black uppercase tracking-widest text-rose-300">Belum ada</span>
        )}
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <div className={`${previewClass} rounded-xl border border-white/10 bg-black/30 flex items-center justify-center overflow-hidden shrink-0`}>
          {value ? (
            <img src={imageUrl(value)} alt={label} className="w-full h-full object-contain" data-testid={`${id}-preview`} />
          ) : (
            <ImageIcon size={22} className="text-white/20" />
          )}
        </div>
        <div className="flex-1 min-w-[160px]">
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={pick} disabled={uploading}
              className="bg-cyan-500/20 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/30 text-xs font-black"
              data-testid={`${id}-upload-btn`}>
              <Upload size={12} className="mr-1" /> {uploading ? "Mengupload…" : value ? "Replace" : "Upload"}
            </Button>
            {value && (
              <Button size="sm" variant="ghost" onClick={() => onChange(null)} disabled={uploading}
                className="text-rose-300/80 hover:text-rose-300 hover:bg-rose-500/10 text-xs font-black"
                data-testid={`${id}-remove-btn`}>
                <Trash2 size={12} className="mr-1" /> Remove
              </Button>
            )}
          </div>
          <p className="text-white/40 text-[10px] mt-2">{hint}</p>
          {value && <p className="text-white/30 text-[10px] font-mono mt-1 truncate">{value}</p>}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon"
        className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${id}-input`} />
    </div>
  );
};

const BrandingTab = ({ design, meta, onLogo, onThumbnail, uploadingKey, setUploadingKey }) => (
  <div className="space-y-4" data-testid="designer-tab-branding">
    <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-3">
      <p className="text-cyan-200 text-xs">
        Upload aset branding template. Perubahan langsung tersimpan ke Template Configuration dan tampil di preview — tanpa refresh.
      </p>
    </div>
    <AssetUploader
      id="brand-logo-uploader" label="Logo"
      value={design.logo_url} previewClass="w-20 h-20"
      hint="Dipakai pada header storefront & validasi publish. PNG/JPG/WebP/SVG, max 2 MB."
      onChange={onLogo}
      uploading={uploadingKey === "logo"}
      setUploading={(v) => setUploadingKey(v ? "logo" : null)}
    />
    <AssetUploader
      id="brand-thumb-uploader" label="Thumbnail"
      value={meta.thumbnail} previewClass="w-32 h-20"
      hint="Dipakai pada Template Registry, Create Tenant & Preview Card. Rasio landscape disarankan, max 2 MB."
      onChange={onThumbnail}
      uploading={uploadingKey === "thumbnail"}
      setUploading={(v) => setUploadingKey(v ? "thumbnail" : null)}
    />
  </div>
);

/* ============ DNA TAB ============ */
const DnaTab = ({ dna, setDna, presets, onApplyPreset }) => {
  const [unitInput, setUnitInput] = useState("");
  const [sectionInput, setSectionInput] = useState("");
  const set = (k, v) => setDna((d) => ({ ...d, [k]: v }));

  const move = (arr, i, dir) => {
    const next = [...arr];
    const j = i + dir;
    if (j < 0 || j >= next.length) return arr;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  };

  return (
    <div className="space-y-4" data-testid="designer-tab-dna">
      <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-3 flex items-center gap-3 flex-wrap">
        <Sparkles size={14} className="text-purple-300" />
        <span className="text-purple-200 text-xs font-bold">Terapkan Business Preset:</span>
        <select id="dna-preset-select" className={`${inputCls} !w-auto`} defaultValue="" data-testid="dna-preset-select">
          <option value="">— pilih preset —</option>
          {presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <Button size="sm" onClick={() => {
          const v = document.getElementById("dna-preset-select").value;
          if (v) onApplyPreset(v);
        }} className="bg-purple-500/80 hover:bg-purple-500 text-white text-xs" data-testid="dna-preset-apply">Apply</Button>
      </div>

      <Field label="Business Category">
        <input value={dna.business_category || ""} onChange={(e) => set("business_category", e.target.value)} className={inputCls} data-testid="dna-business-category" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Banner Style" value={dna.banner_style} onChange={(v) => set("banner_style", v)} options={["slider", "static", "none"]} testid="dna-banner-style" />
        <SelectField label="Product Layout" value={dna.product_layout} onChange={(v) => set("product_layout", v)} options={["grid-2-col", "grid-3-col", "grid-4-col", "list"]} testid="dna-product-layout" />
        <SelectField label="Category Style" value={dna.category_style} onChange={(v) => set("category_style", v)} options={["sidebar", "top-chips", "dropdown", "mega-menu"]} testid="dna-category-style" />
        <SelectField label="Search Style" value={dna.search_style} onChange={(v) => set("search_style", v)} options={["instant-suggestions", "simple", "fullscreen-overlay"]} testid="dna-search-style" />
        <SelectField label="Product Card" value={dna.product_card} onChange={(v) => set("product_card", v)} options={["compact-image-top", "image-left", "overlay", "minimal"]} testid="dna-product-card" />
        <SelectField label="Checkout Style" value={dna.checkout_style} onChange={(v) => set("checkout_style", v)} options={["single-page-stepper", "multi-step", "one-click"]} testid="dna-checkout-style" />
      </div>

      <Field label={`Inventory Units (${(dna.inventory_units || []).length})`}>
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="dna-units-list">
          {(dna.inventory_units || []).map((u, i) => (
            <span key={`${u}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 px-2.5 py-1 text-xs font-bold">
              {u}
              <button onClick={() => set("inventory_units", dna.inventory_units.filter((_, x) => x !== i))} className="text-cyan-300/70 hover:text-rose-300" data-testid={`dna-unit-remove-${i}`}><Trash2 size={10} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={unitInput} onChange={(e) => setUnitInput(e.target.value)} placeholder="mis. Butir, Tray, Kg…" className={inputCls} data-testid="dna-unit-input"
            onKeyDown={(e) => { if (e.key === "Enter" && unitInput.trim()) { set("inventory_units", [...(dna.inventory_units || []), unitInput.trim()]); setUnitInput(""); } }} />
          <Button size="sm" onClick={() => { if (unitInput.trim()) { set("inventory_units", [...(dna.inventory_units || []), unitInput.trim()]); setUnitInput(""); } }} className="bg-white/10 hover:bg-white/20 text-white" data-testid="dna-unit-add"><Plus size={14} /></Button>
        </div>
      </Field>

      <Field label={`Homepage Sections (${(dna.homepage_sections || []).length})`}>
        <div className="space-y-1.5 mb-2" data-testid="dna-sections-list">
          {(dna.homepage_sections || []).map((s, i) => (
            <div key={`${s}-${i}`} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white">
              <span className="text-white/40 font-mono w-4">{i + 1}</span>
              <span className="flex-1 font-bold">{s}</span>
              <button onClick={() => set("homepage_sections", move(dna.homepage_sections, i, -1))} className="text-white/50 hover:text-white"><ArrowUp size={12} /></button>
              <button onClick={() => set("homepage_sections", move(dna.homepage_sections, i, 1))} className="text-white/50 hover:text-white"><ArrowDown size={12} /></button>
              <button onClick={() => set("homepage_sections", dna.homepage_sections.filter((_, x) => x !== i))} className="text-rose-300/70 hover:text-rose-300" data-testid={`dna-section-remove-${i}`}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={sectionInput} onChange={(e) => setSectionInput(e.target.value)} placeholder="mis. New Arrival, Promo…" className={inputCls} data-testid="dna-section-input"
            onKeyDown={(e) => { if (e.key === "Enter" && sectionInput.trim()) { set("homepage_sections", [...(dna.homepage_sections || []), sectionInput.trim()]); setSectionInput(""); } }} />
          <Button size="sm" onClick={() => { if (sectionInput.trim()) { set("homepage_sections", [...(dna.homepage_sections || []), sectionInput.trim()]); setSectionInput(""); } }} className="bg-white/10 hover:bg-white/20 text-white" data-testid="dna-section-add"><Plus size={14} /></Button>
        </div>
      </Field>
    </div>
  );
};

/* ============ DESIGN TAB ============ */
const DesignTab = ({ design, setDesign }) => {
  const set = (k, v) => setDesign((d) => ({ ...d, [k]: v }));
  return (
    <div className="space-y-4" data-testid="designer-tab-design">
      <div className="grid grid-cols-2 gap-3">
        <ColorField label="Primary Color" value={design.primary_color} onChange={(v) => set("primary_color", v)} testid="design-primary-color" />
        <ColorField label="Accent Color" value={design.accent_color} onChange={(v) => set("accent_color", v)} testid="design-accent-color" />
        <ColorField label="Secondary (Footer)" value={design.secondary_color} onChange={(v) => set("secondary_color", v)} testid="design-secondary-color" />
        <ColorField label="Background" value={design.background_color} onChange={(v) => set("background_color", v)} testid="design-background-color" />
        <ColorField label="Text Color" value={design.text_color} onChange={(v) => set("text_color", v)} testid="design-text-color" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Typography Heading" value={design.typography_heading} onChange={(v) => set("typography_heading", v)} options={FONTS} testid="design-font-heading" />
        <SelectField label="Typography Body" value={design.typography_body} onChange={(v) => set("typography_body", v)} options={FONTS} testid="design-font-body" />
        <SelectField label="Header Style" value={design.header_style} onChange={(v) => set("header_style", v)} options={["sticky-with-search", "simple", "centered-logo", "transparent"]} testid="design-header-style" />
        <SelectField label="Footer Style" value={design.footer_style} onChange={(v) => set("footer_style", v)} options={["columns-4", "columns-2", "minimal", "centered"]} testid="design-footer-style" />
        <SelectField label="Homepage Layout" value={design.homepage_layout} onChange={(v) => set("homepage_layout", v)} options={["banner-sections-grid", "hero-full", "magazine", "minimal-list"]} testid="design-homepage-layout" />
        <SelectField label="Category Layout" value={design.category_layout} onChange={(v) => set("category_layout", v)} options={["sidebar", "top-chips", "grid-tiles"]} testid="design-category-layout" />
        <SelectField label="Product Card Style" value={design.product_card_style} onChange={(v) => set("product_card_style", v)} options={["compact-image-top", "rounded", "sharp", "overlay"]} testid="design-card-style" />
        <SelectField label="Button Style" value={design.button_style} onChange={(v) => set("button_style", v)} options={["rounded", "pill", "sharp"]} testid="design-button-style" />
        <SelectField label="Icon Style" value={design.icon_style} onChange={(v) => set("icon_style", v)} options={["outline", "filled", "duotone"]} testid="design-icon-style" />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" id="banner-slider" checked={!!design.banner_slider} onChange={(e) => set("banner_slider", e.target.checked)} className="accent-cyan-500" data-testid="design-banner-slider" />
        <label htmlFor="banner-slider" className="text-white/80 text-sm font-bold">Banner Slider aktif</label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Logo URL"><input value={design.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className={inputCls} data-testid="design-logo-url" /></Field>
        <Field label="Favicon URL"><input value={design.favicon_url || ""} onChange={(e) => set("favicon_url", e.target.value)} className={inputCls} data-testid="design-favicon-url" /></Field>
      </div>
      <Field label="Empty State Text"><input value={design.empty_state_text || ""} onChange={(e) => set("empty_state_text", e.target.value)} className={inputCls} data-testid="design-empty-state" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="404 Title"><input value={design.notfound_title || ""} onChange={(e) => set("notfound_title", e.target.value)} className={inputCls} data-testid="design-404-title" /></Field>
        <Field label="404 Message"><input value={design.notfound_message || ""} onChange={(e) => set("notfound_message", e.target.value)} className={inputCls} data-testid="design-404-message" /></Field>
      </div>
      <Field label="SEO Default Title"><input value={design.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} data-testid="design-seo-title" /></Field>
      <Field label="SEO Default Description"><textarea rows={2} value={design.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={inputCls} data-testid="design-seo-description" /></Field>
      <Field label="Business Description"><textarea rows={3} value={design.business_description || ""} onChange={(e) => set("business_description", e.target.value)} className={inputCls} data-testid="design-business-description" /></Field>
    </div>
  );
};

/* ============ DEMO CONTENT TAB ============ */
const DemoTab = ({ demo, setDemo, units }) => {
  const set = (k, v) => setDemo((d) => ({ ...d, [k]: v }));
  const setRow = (key, i, field, v) => set(key, demo[key].map((r, x) => (x === i ? { ...r, [field]: v } : r)));
  const addRow = (key, row) => set(key, [...(demo[key] || []), row]);
  const delRow = (key, i) => set(key, demo[key].filter((_, x) => x !== i));
  const rowCls = "rounded-xl border border-white/10 bg-white/5 p-3 space-y-2";

  return (
    <div className="space-y-5" data-testid="designer-tab-demo">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Demo Brand Name"><input value={demo.brand_name || ""} onChange={(e) => set("brand_name", e.target.value)} className={inputCls} data-testid="demo-brand-name" /></Field>
        <Field label="Demo Logo URL"><input value={demo.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className={inputCls} data-testid="demo-logo-url" /></Field>
        <Field label="Demo SEO Title"><input value={demo.seo?.title || ""} onChange={(e) => set("seo", { ...demo.seo, title: e.target.value })} className={inputCls} data-testid="demo-seo-title" /></Field>
        <Field label="Demo SEO Description"><input value={demo.seo?.description || ""} onChange={(e) => set("seo", { ...demo.seo, description: e.target.value })} className={inputCls} data-testid="demo-seo-description" /></Field>
      </div>

      {/* Categories */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className={labelCls}>Demo Categories ({(demo.categories || []).length}/20)</span>
          <Button size="sm" onClick={() => addRow("categories", { name: "", slug: "", icon: "", order: (demo.categories || []).length })} className="bg-white/10 hover:bg-white/20 text-white text-xs" data-testid="demo-cat-add"><Plus size={12} className="mr-1" />Kategori</Button>
        </div>
        <div className="space-y-2">
          {(demo.categories || []).map((c, i) => (
            <div key={i} className={rowCls} data-testid={`demo-cat-row-${i}`}>
              <div className="grid grid-cols-[1fr_1fr_80px_32px] gap-2 items-center">
                <input value={c.name || ""} onChange={(e) => setRow("categories", i, "name", e.target.value)} placeholder="Nama" className={inputCls} data-testid={`demo-cat-name-${i}`} />
                <input value={c.slug || ""} onChange={(e) => setRow("categories", i, "slug", e.target.value)} placeholder="slug" className={`${inputCls} font-mono`} />
                <input value={c.icon || ""} onChange={(e) => setRow("categories", i, "icon", e.target.value)} placeholder="Icon" className={inputCls} />
                <button onClick={() => delRow("categories", i)} className="text-rose-300/70 hover:text-rose-300" data-testid={`demo-cat-del-${i}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Products */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className={labelCls}>Demo Products ({(demo.products || []).length}/50)</span>
          <Button size="sm" onClick={() => addRow("products", { name: "", price: 0, unit: units[0] || "pcs", stock: 10, category_slug: demo.categories?.[0]?.slug || "", is_featured: false, image_url: "" })} className="bg-white/10 hover:bg-white/20 text-white text-xs" data-testid="demo-prod-add"><Plus size={12} className="mr-1" />Produk</Button>
        </div>
        <div className="space-y-2">
          {(demo.products || []).map((p, i) => (
            <div key={i} className={rowCls} data-testid={`demo-prod-row-${i}`}>
              <div className="grid grid-cols-[2fr_1fr_32px] gap-2">
                <input value={p.name || ""} onChange={(e) => setRow("products", i, "name", e.target.value)} placeholder="Nama produk" className={inputCls} data-testid={`demo-prod-name-${i}`} />
                <input type="number" value={p.price ?? 0} onChange={(e) => setRow("products", i, "price", Number(e.target.value))} placeholder="Harga" className={inputCls} data-testid={`demo-prod-price-${i}`} />
                <button onClick={() => delRow("products", i)} className="text-rose-300/70 hover:text-rose-300" data-testid={`demo-prod-del-${i}`}><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <select value={p.unit || ""} onChange={(e) => setRow("products", i, "unit", e.target.value)} className={inputCls}>
                  {units.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <input type="number" value={p.stock ?? 0} onChange={(e) => setRow("products", i, "stock", Number(e.target.value))} placeholder="Stok" className={inputCls} />
                <select value={p.category_slug || ""} onChange={(e) => setRow("products", i, "category_slug", e.target.value)} className={inputCls}>
                  <option value="">— kategori —</option>
                  {(demo.categories || []).map((c) => <option key={c.slug} value={c.slug}>{c.name || c.slug}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-white/70 text-xs">
                  <input type="checkbox" checked={!!p.is_featured} onChange={(e) => setRow("products", i, "is_featured", e.target.checked)} className="accent-cyan-500" /> Featured
                </label>
              </div>
              <input value={p.image_url || ""} onChange={(e) => setRow("products", i, "image_url", e.target.value)} placeholder="Image URL (opsional)" className={inputCls} />
            </div>
          ))}
        </div>
      </div>

      {/* Banners */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className={labelCls}>Demo Banners ({(demo.banners || []).length}/10)</span>
          <Button size="sm" onClick={() => addRow("banners", { title: "", subtitle: "", image_url: "", link_url: "", badge: "", button_text: "", button_link: "", mobile_image_url: "", text_alignment: "left", overlay_strength: 60, order: (demo.banners || []).length })} className="bg-white/10 hover:bg-white/20 text-white text-xs" data-testid="demo-banner-add"><Plus size={12} className="mr-1" />Banner</Button>
        </div>
        <div className="space-y-2">
          {(demo.banners || []).map((b, i) => (
            <div key={i} className={rowCls} data-testid={`demo-banner-row-${i}`}>
              <div className="grid grid-cols-[1fr_1fr_32px] gap-2">
                <input value={b.title || ""} onChange={(e) => setRow("banners", i, "title", e.target.value)} placeholder="Judul" className={inputCls} data-testid={`demo-banner-title-${i}`} />
                <input value={b.subtitle || ""} onChange={(e) => setRow("banners", i, "subtitle", e.target.value)} placeholder="Subjudul" className={inputCls} />
                <button onClick={() => delRow("banners", i)} className="text-rose-300/70 hover:text-rose-300" data-testid={`demo-banner-del-${i}`}><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input value={b.image_url || ""} onChange={(e) => setRow("banners", i, "image_url", e.target.value)} placeholder="Image URL (desktop)" className={inputCls} />
                <input value={b.mobile_image_url || ""} onChange={(e) => setRow("banners", i, "mobile_image_url", e.target.value)} placeholder="Image URL mobile (opsional)" className={inputCls} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input value={b.badge || ""} onChange={(e) => setRow("banners", i, "badge", e.target.value)} placeholder="Badge" className={inputCls} data-testid={`demo-banner-badge-${i}`} />
                <input value={b.button_text || ""} onChange={(e) => setRow("banners", i, "button_text", e.target.value)} placeholder="Teks tombol" className={inputCls} data-testid={`demo-banner-button-text-${i}`} />
                <input value={b.button_link || b.link_url || ""} onChange={(e) => setRow("banners", i, "button_link", e.target.value)} placeholder="Link tombol" className={inputCls} />
              </div>
              <div className="grid grid-cols-[1fr_1fr_80px] gap-2">
                <select value={b.text_alignment || "left"} onChange={(e) => setRow("banners", i, "text_alignment", e.target.value)} className={inputCls} data-testid={`demo-banner-align-${i}`}>
                  <option value="left">Teks kiri</option>
                  <option value="center">Teks tengah</option>
                  <option value="right">Teks kanan</option>
                </select>
                <input type="number" min={0} max={100} value={b.overlay_strength ?? 60} onChange={(e) => setRow("banners", i, "overlay_strength", Number(e.target.value))} placeholder="Overlay %" className={inputCls} title="Kekuatan overlay gelap (0-100)" />
                <input type="number" value={b.order ?? i} onChange={(e) => setRow("banners", i, "order", Number(e.target.value))} placeholder="Urut" className={inputCls} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ============ METADATA TAB ============ */
const MetadataTab = ({ meta, setMeta, tpl, onStatusChange }) => {
  const set = (k, v) => setMeta((m) => ({ ...m, [k]: v }));
  return (
    <div className="space-y-4" data-testid="designer-tab-metadata">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Template Name"><input value={meta.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="meta-name" /></Field>
        <Field label="Slug (immutable)"><input value={tpl.slug} disabled className={`${inputCls} font-mono opacity-50`} /></Field>
        <SelectField label="Category" value={meta.category} onChange={(v) => set("category", v)} options={CATEGORIES} testid="meta-category" />
        <Field label="Version"><input value={meta.version || ""} onChange={(e) => set("version", e.target.value)} className={inputCls} data-testid="meta-version" /></Field>
      </div>
      <Field label="Thumbnail URL"><input value={meta.thumbnail || ""} onChange={(e) => set("thumbnail", e.target.value)} className={inputCls} data-testid="meta-thumbnail" /></Field>
      <Field label="Description"><textarea rows={3} value={meta.description || ""} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="meta-description" /></Field>
      <div className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center gap-3 flex-wrap">
        <span className="text-white/70 text-xs font-bold">Status: <span className="text-cyan-300">{tpl.status}</span></span>
        {["DRAFT", "PUBLISHED", "ARCHIVED"].filter((s) => s !== tpl.status).map((s) => (
          <Button key={s} size="sm" variant="ghost" onClick={() => onStatusChange(s)} className="text-white/70 hover:text-white hover:bg-white/10 text-xs" data-testid={`meta-status-${s.toLowerCase()}`}>→ {s}</Button>
        ))}
        {tpl.is_system && <span className="text-amber-300/80 text-[10px] font-mono uppercase">System template — tidak bisa archive/deprecate</span>}
      </div>
    </div>
  );
};

/* ============ TEMPLATE READINESS (mirror validate_for_publish backend) ============ */
const buildChecklist = ({ design, dna, meta, demo, tpl }) => [
  { key: "logo", label: "Logo", group: "Branding", required: true,
    ok: !!(design.logo_url || tpl.branding?.logo_url), tab: "branding", target: "brand-logo-uploader", reason: "Logo belum diupload" },
  { key: "thumbnail", label: "Thumbnail", group: "Branding", required: true,
    ok: !!(meta.thumbnail || "").trim(), tab: "branding", target: "brand-thumb-uploader", reason: "Thumbnail belum diupload" },
  { key: "name", label: "Name", group: "Metadata", required: true,
    ok: !!(meta.name || "").trim(), tab: "metadata", target: "meta-name", reason: "Nama template belum diisi" },
  { key: "slug", label: "Slug", group: "Metadata", required: true,
    ok: !!tpl.slug, tab: "metadata", target: "meta-name", reason: "Slug belum diisi" },
  { key: "category", label: "Category", group: "Metadata", required: true,
    ok: CATEGORIES.includes(meta.category), tab: "metadata", target: "meta-category", reason: "Kategori belum valid" },
  { key: "description", label: "Description", group: "Metadata", required: true,
    ok: !!(meta.description || "").trim(), tab: "metadata", target: "meta-description", reason: "Deskripsi belum diisi" },
  { key: "homepage", label: "Homepage", group: "DNA", required: true,
    ok: (dna.homepage_sections || []).length > 0, tab: "dna", target: "dna-section-input", reason: "Homepage belum dikonfigurasi" },
  { key: "product_grid", label: "Product Grid", group: "DNA", required: true,
    ok: !!(dna.product_layout || "").trim(), tab: "dna", target: "dna-product-layout", reason: "Product Grid belum dikonfigurasi" },
  { key: "preview", label: "Preview berhasil dibuat", group: "Preview", required: true,
    ok: !!(design && dna), tab: "design", target: "design-primary-color", reason: "Preview gagal dibuat" },
  { key: "demo_categories", label: "Kategori", group: "Demo Content", required: false,
    ok: (demo.categories || []).length > 0, tab: "demo", target: "demo-cat-add", reason: "Demo kategori kosong" },
  { key: "demo_products", label: "Produk", group: "Demo Content", required: false,
    ok: (demo.products || []).length > 0, tab: "demo", target: "demo-prod-add", reason: "Demo produk kosong" },
  { key: "demo_banners", label: "Banner", group: "Demo Content", required: false,
    ok: (demo.banners || []).length > 0, tab: "demo", target: "demo-banner-add", reason: "Demo banner kosong" },
];

const ReadinessItem = ({ item, onJump }) => {
  const Icon = item.ok ? CheckCircle2 : item.required ? XCircle : AlertTriangle;
  const tone = item.ok ? "text-emerald-400" : item.required ? "text-rose-400" : "text-amber-400";
  return (
    <button
      onClick={() => onJump(item)}
      className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs hover:bg-white/10 transition-colors"
      data-testid={`readiness-item-${item.key}`}
      data-status={item.ok ? "ready" : item.required ? "missing" : "warning"}
    >
      <Icon size={13} className={`${tone} shrink-0`} />
      <span className={`flex-1 font-bold ${item.ok ? "text-white/80" : "text-white/60"}`}>{item.label}</span>
      {!item.ok && (
        <span className={`text-[9px] font-black uppercase tracking-widest ${item.required ? "text-rose-300" : "text-amber-300"}`}>
          {item.required ? "Missing" : "Warning"}
        </span>
      )}
    </button>
  );
};

const ReadinessPanel = ({ checklist, tpl, onJump, onPublish, publishing }) => {
  const required = checklist.filter((c) => c.required);
  const done = required.filter((c) => c.ok).length;
  const pct = required.length ? Math.round((done / required.length) * 100) : 0;
  const ready = done === required.length;
  const blockers = required.filter((c) => !c.ok);
  const isPublished = ["PUBLISHED", "ACTIVE"].includes(tpl.status);
  const groups = [...new Set(checklist.map((c) => c.group))];

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-4 mb-4" data-testid="readiness-panel">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardCheck size={14} className="text-cyan-400" />
        <span className="text-white/80 text-xs font-black uppercase tracking-widest">Template Readiness</span>
        <span className="ml-auto text-white/60 text-xs font-mono" data-testid="readiness-progress">{done} / {required.length} · {pct}%</span>
      </div>

      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-4">
        <div
          className={`h-full rounded-full transition-all duration-500 ${ready ? "bg-gradient-to-r from-emerald-500 to-cyan-400" : "bg-gradient-to-r from-cyan-500 to-purple-500"}`}
          style={{ width: `${pct}%` }}
          data-testid="readiness-progress-bar"
        />
      </div>

      <div className="space-y-3 mb-4">
        {groups.map((g) => (
          <div key={g}>
            <div className="text-[9px] uppercase tracking-widest text-white/40 font-black mb-1 px-2">{g}</div>
            {checklist.filter((c) => c.group === g).map((item) => (
              <ReadinessItem key={item.key} item={item} onJump={onJump} />
            ))}
          </div>
        ))}
      </div>

      {isPublished ? (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2" data-testid="readiness-published-badge">
          <CheckCircle2 size={14} className="text-emerald-400" />
          <span className="text-emerald-200 text-xs font-black uppercase tracking-widest">Published</span>
          <span className="text-emerald-300/60 text-[10px] ml-auto">Edit tersimpan langsung live</span>
        </div>
      ) : (
        <>
          <Button
            onClick={onPublish}
            disabled={!ready || publishing}
            className={`w-full font-black ${ready ? "bg-gradient-to-r from-emerald-500 to-cyan-500 text-white" : "bg-white/10 text-white/40 cursor-not-allowed"}`}
            data-testid="readiness-publish-btn"
          >
            <Rocket size={13} className="mr-1.5" />
            {publishing ? "Publishing…" : ready ? "Ready to Publish — Publish Sekarang" : "Publish"}
          </Button>
          {!ready && (
            <div className="mt-2 rounded-lg border border-rose-500/20 bg-rose-500/5 px-3 py-2" data-testid="readiness-publish-blockers">
              <div className="text-rose-300 text-[10px] font-black uppercase tracking-widest mb-1">Cannot Publish</div>
              {blockers.slice(0, 4).map((b) => (
                <div key={b.key} className="text-rose-200/80 text-[11px]">• {b.reason}</div>
              ))}
              {blockers.length > 4 && <div className="text-rose-200/50 text-[10px]">+{blockers.length - 4} lainnya</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ============ PAGE ============ */
const TABS = [
  { key: "branding", label: "Branding", icon: ImageIcon },
  { key: "design", label: "Design", icon: Palette },
  { key: "dna", label: "DNA", icon: Dna },
  { key: "demo", label: "Demo Content", icon: Package2 },
  { key: "metadata", label: "Metadata", icon: FileText },
];

export default function TemplateDesigner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("design");
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState([]);
  const [dna, setDna] = useState({});
  const [design, setDesign] = useState({});
  const [demo, setDemo] = useState({});
  const [meta, setMeta] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/platform/template-registry/${id}`);
      const t = r.data;
      setTpl(t);
      setDna(t.dna || {});
      setDesign(t.design || {});
      setDemo(t.demo_content || { categories: [], products: [], banners: [], seo: {} });
      setMeta({ name: t.name, description: t.description, category: t.category, thumbnail: t.thumbnail || "", version: t.version });
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else notifyError(e);
    }
  }, [id]);

  useEffect(() => {
    load();
    api.get("/platform/template-registry/dna-presets").then((r) => setPresets(r.data.items || [])).catch(() => {});
  }, [load]);

  const saveSection = async () => {
    setSaving(true);
    try {
      let r;
      if (tab === "dna") r = await api.put(`/platform/template-registry/${id}/dna`, dna);
      else if (tab === "design" || tab === "branding") r = await api.put(`/platform/template-registry/${id}/design`, design);
      else if (tab === "demo") r = await api.put(`/platform/template-registry/${id}/demo-content`, demo);
      else r = await api.put(`/platform/template-registry/${id}`, meta);
      setTpl(r.data);
      setDna(r.data.dna || {});
      setDesign(r.data.design || {});
      setDemo(r.data.demo_content || {});
      toast.success("Perubahan tersimpan.");
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  };

  // Branding uploaders — langsung persist ke Template Configuration (no refresh).
  const persistLogo = async (url) => {
    const next = { ...design, logo_url: url || null };
    setDesign(next);
    try {
      const r = await api.put(`/platform/template-registry/${id}/design`, next);
      setTpl(r.data);
      setDesign(r.data.design || next);
    } catch (e) { notifyError(e, "Gagal menyimpan logo"); }
  };

  const persistThumbnail = async (url) => {
    const next = { ...meta, thumbnail: url || "" };
    setMeta(next);
    try {
      const r = await api.put(`/platform/template-registry/${id}`, next);
      setTpl(r.data);
    } catch (e) { notifyError(e, "Gagal menyimpan thumbnail"); }
  };

  // Template Readiness — mirror aturan validate_for_publish backend, live dari state.
  const checklist = useMemo(
    () => (tpl ? buildChecklist({ design, dna, meta, demo, tpl }) : []),
    [design, dna, meta, demo, tpl]
  );

  // Click to Fix — pindah tab + scroll/fokus ke elemen terkait.
  const jumpTo = (item) => {
    setTab(item.tab);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${item.target}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") el.focus({ preventScroll: true });
      }
    }, 180);
  };

  // Publish Shortcut — simpan semua section dulu supaya validasi server melihat
  // state terbaru, lalu jalankan publish validation existing di backend.
  const publishNow = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      await api.put(`/platform/template-registry/${id}`, meta);
      await api.put(`/platform/template-registry/${id}/design`, design);
      await api.put(`/platform/template-registry/${id}/dna`, dna);
      await api.put(`/platform/template-registry/${id}/demo-content`, demo);
      const r = await api.post(`/platform/template-registry/${id}/publish`);
      setTpl(r.data.template);
      toast.success(`Template "${r.data.template.name}" berhasil di-publish.`);
      (r.data.warnings || []).forEach((w) => toast.warning(w));
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && Array.isArray(d.errors)) d.errors.forEach((err) => toast.error(err));
      else notifyError(e, "Gagal publish template");
    } finally {
      setPublishing(false);
    }
  };

  const applyPreset = async (presetKey) => {
    try {
      const r = await api.post(`/platform/template-registry/${id}/apply-dna-preset`, { preset: presetKey });
      setTpl(r.data);
      setDna(r.data.dna || {});
      toast.success(`DNA preset "${presetKey}" diterapkan.`);
    } catch (e) { notifyError(e); }
  };

  const changeStatus = async (status) => {
    try {
      const r = await api.put(`/platform/template-registry/${id}/status`, { status });
      setTpl(r.data);
      toast.success(`Status template → ${status}.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && Array.isArray(d.errors)) d.errors.forEach((err) => toast.error(err));
      else notifyError(e);
    }
  };

  if (notFound) {
    return (
      <PlatformLayout title="Template Designer">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center text-white/50" data-testid="designer-not-found">
          Template tidak ditemukan.
          <div className="mt-3"><Button variant="ghost" onClick={() => navigate("/platform/template-registry")} className="text-cyan-300">← Kembali ke Registry</Button></div>
        </div>
      </PlatformLayout>
    );
  }

  if (!tpl) {
    return <PlatformLayout title="Template Designer"><div className="text-white/50 text-sm" data-testid="designer-loading">Memuat template…</div></PlatformLayout>;
  }

  return (
    <PlatformLayout title="Template Designer">
      <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="designer-header">
        <Button variant="ghost" onClick={() => navigate("/platform/template-registry")} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="designer-back">
          <ArrowLeft size={14} className="mr-1" /> Registry
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-white font-display font-black text-2xl truncate">{tpl.name}</h1>
          <div className="text-white/50 text-xs font-mono">{tpl.slug} · v{tpl.version} · {tpl.status} · {tpl.category}</div>
        </div>
        <Button onClick={saveSection} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-black" data-testid="designer-save">
          <Save size={14} className="mr-1" /> {saving ? "Menyimpan…" : `Simpan ${TABS.find((t) => t.key === tab)?.label}`}
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
        <div>
          <div className="flex items-center gap-1 border-b border-white/10 mb-4" data-testid="designer-tabs">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-black uppercase tracking-widest border-b-2 transition-colors ${tab === t.key ? "border-cyan-400 text-cyan-300" : "border-transparent text-white/50 hover:text-white/80"}`}
                data-testid={`designer-tab-btn-${t.key}`}>
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>
          {tab === "branding" && (
            <BrandingTab design={design} meta={meta} onLogo={persistLogo} onThumbnail={persistThumbnail}
              uploadingKey={uploadingKey} setUploadingKey={setUploadingKey} />
          )}
          {tab === "design" && <DesignTab design={design} setDesign={setDesign} />}
          {tab === "dna" && <DnaTab dna={dna} setDna={setDna} presets={presets} onApplyPreset={applyPreset} />}
          {tab === "demo" && <DemoTab demo={demo} setDemo={setDemo} units={dna.inventory_units || ["pcs"]} />}
          {tab === "metadata" && <MetadataTab meta={meta} setMeta={setMeta} tpl={tpl} onStatusChange={changeStatus} />}
        </div>

        <div className="xl:sticky xl:top-4 self-start">
          <ReadinessPanel checklist={checklist} tpl={tpl} onJump={jumpTo} onPublish={publishNow} publishing={publishing} />
          <div className="flex items-center gap-2 mb-2 text-white/60 text-xs font-black uppercase tracking-widest">
            <Eye size={13} className="text-cyan-400" /> Live Preview
          </div>
          <TemplateLivePreview design={design} dna={dna} demoContent={demo} />
          <p className="text-white/30 text-[10px] mt-2 text-center">Preview homepage berdasarkan Design + DNA + Demo Content (belum tersimpan ikut tampil).</p>
        </div>
      </div>
    </PlatformLayout>
  );
}
